import React from 'react';
import PropTypes from 'prop-types';
import ExpandableSection from '../../expandableSection';
import { ActionButtons } from './sectionShared';

const formatNumber = (value) => Number(value || 0).toFixed(2);
const getEffortHoursForDisplay = (row = {}) => (
  row.efforts_hours_display ?? row.estimated_efforts ?? row.efforts_hour ?? row.efforts_hours
);

const EffortsSection = ({
  expanded,
  onToggle,
  onDownload,
  onMoreMenuItems,
  onMore,
  showMoreAction,
  showActions,
  canEditEffort,
  canDeleteEffort,
  effortsData,
  expandedEffortRows,
  toggleEffortRow,
  handleEffortEdit,
  handleEffortDelete,
  isLoading,
}) => {
  const rows = Array.isArray(effortsData?.rows) ? effortsData.rows : [];
  const rawGroups = Array.isArray(effortsData?.groups) ? effortsData.groups : [];
  const groups = [...rawGroups].sort((firstGroup, secondGroup) => {
    if (firstGroup?.isBaseAddedGroup && !secondGroup?.isBaseAddedGroup) return -1;
    if (!firstGroup?.isBaseAddedGroup && secondGroup?.isBaseAddedGroup) return 1;

    if (firstGroup?.isInitialGroup && !secondGroup?.isInitialGroup) return 1;
    if (!firstGroup?.isInitialGroup && secondGroup?.isInitialGroup) return -1;

    return Number(firstGroup?.displayOrder || 0) - Number(secondGroup?.displayOrder || 0);
  });

  const canShowChildActionsForGroup = (__groupIndex) => true;

  const totalEffortHours = groups.length
    ? groups.reduce((sum, group) => sum + Number(group?.totalHours || 0), 0)
    : rows.reduce((sum, row) => sum + Number(getEffortHoursForDisplay(row) || 0), 0);

  return (
    <ExpandableSection
      title="Effort Estimation"
      expanded={expanded}
      onToggle={onToggle}
      onDownload={onDownload}
      onMoreMenuItems={onMoreMenuItems}
      onMore={onMore}
      showMoreAction={showMoreAction}
      isLoading={isLoading}
    >
      <div className="section-content efforts-section">
        <div className="efforts-table-wrapper">
          <div className="efforts-table-scroll">
            <table className="efforts-data-table efforts-data-table--grouped">
              <thead>
                <tr>
                  <th className="th-cell">CR Number</th>
                  <th className="th-cell">CR Name</th>
                  <th className="th-cell">Effort Investment</th>
                  <th className="th-cell">CR Status</th>
                  <th className="th-cell">Customer Approval Status</th>
                  <th className="th-cell">Resource Category</th>
                  <th className="th-cell">Resource Skills</th>
                  <th className="th-cell">Effort (Hrs)</th>
                  <th className="th-cell">Details</th>
                  {showActions ? (
                    <th className="th-cell th-actions">
                      <div className="th-content th-actions-content">
                        <span>Actions</span>
                      </div>
                    </th>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {!groups.length ? (
                  <tr className="tr-data">
                    <td className="td-cell td-empty-state" colSpan={showActions ? 10 : 9}>
                      No effort estimation records available
                    </td>
                  </tr>
                ) : (
                  <>
                    {groups.map((group, groupIndex) => {
                      const groupKey = String(group.row_id ?? group.id);
                      const isExpanded = expandedEffortRows?.[groupKey] ?? false;

                      return (
                        <React.Fragment key={groupKey}>
                          <tr className="tr-data parent-row">
                            <td className="td-cell td-cr-number">
                              <div className="grouped-row-cell">
                                <button
                                  type="button"
                                  className={`expand-row-btn effort-expand-btn ${isExpanded ? 'expanded' : ''}`}
                                  aria-label={isExpanded ? 'Collapse' : 'Expand'}
                                  aria-expanded={isExpanded}
                                  onClick={() => toggleEffortRow(groupKey)}
                                >
                                  Expand
                                </button>
                                <span>{group.crNumber || '-'}</span>
                              </div>
                            </td>
                            <td className="td-cell">{group.crName || '-'}</td>
                            <td className="td-cell">{group.effortInvestment || '-'}</td>
                            <td className="td-cell">{group.crStatus || '-'}</td>
                            <td className="td-cell">{group.customerApprovalStatus || '-'}</td>
                            <td className="td-cell">-</td>
                            <td className="td-cell">-</td>
                            <td className="td-cell td-number-cell">{formatNumber(group.totalHours)}</td>
                            <td className="td-cell">-</td>
                            {showActions ? <td className="td-cell td-actions" /> : null}
                          </tr>
                          {isExpanded
                            ? (group.children || []).map((row, index) => {
                              const canShowActionsForRow = canShowChildActionsForGroup(groupIndex);
                              const canShowEditAction = canEditEffort && canShowActionsForRow;
                              const canShowDeleteAction = canDeleteEffort && canShowActionsForRow;

                              return (
                                <tr key={String(row.row_id ?? row.effort_id ?? index)} className="tr-data child-row">
                                  <td className="td-cell" />
                                  <td className="td-cell" />
                                  <td className="td-cell" />
                                  <td className="td-cell" />
                                  <td className="td-cell" />
                                  <td className="td-cell">{row.resource_category || '-'}</td>
                                  <td className="td-cell">{row.resource_type || '-'}</td>
                                  <td className="td-cell td-number-cell">
                                    {formatNumber(getEffortHoursForDisplay(row))}
                                  </td>
                                  <td className="td-cell">{row.details || '-'}</td>
                                  {showActions ? (
                                    <td className="td-cell td-actions">
                                      <ActionButtons
                                        onEdit={canShowEditAction ? () => handleEffortEdit(row) : null}
                                        onDelete={canShowDeleteAction ? () => handleEffortDelete(row) : null}
                                      />
                                    </td>
                                  ) : null}
                                </tr>
                              );
                            })
                            : null}
                        </React.Fragment>
                      );
                    })}
                    <tr className="tr-data total-row">
                      <td className="td-cell">Total</td>
                      <td className="td-cell" />
                      <td className="td-cell" />
                      <td className="td-cell" />
                      <td className="td-cell" />
                      <td className="td-cell" />
                      <td className="td-cell" />
                      <td className="td-cell td-number-cell">{formatNumber(totalEffortHours)}</td>
                      <td className="td-cell" />
                      {showActions ? <td className="td-cell td-actions" /> : null}
                    </tr>
                  </>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </ExpandableSection>
  );
};

EffortsSection.propTypes = {
  expanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  onDownload: PropTypes.func,
  onMoreMenuItems: PropTypes.array,
  onMore: PropTypes.func,
  showMoreAction: PropTypes.bool,
  showActions: PropTypes.bool,
  canEditEffort: PropTypes.bool,
  canDeleteEffort: PropTypes.bool,
  effortsData: PropTypes.shape({
    rows: PropTypes.arrayOf(PropTypes.object),
    groups: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  expandedEffortRows: PropTypes.object.isRequired,
  toggleEffortRow: PropTypes.func.isRequired,
  handleEffortEdit: PropTypes.func.isRequired,
  handleEffortDelete: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
};

EffortsSection.defaultProps = {
  onDownload: null,
  onMoreMenuItems: [],
  onMore: null,
  showMoreAction: false,
  showActions: false,
  canEditEffort: false,
  canDeleteEffort: false,
  isLoading: false,
};

export default EffortsSection;
